import { templatesForTopic, TEMPLATE_BY_ID } from '../content/templates';
import { TOPICS } from '../content/topics';
import type { Question, Response, TopicId } from '../content/types';
import { generate, isCorrect } from '../engine/generate';
import { awardBadges, recordPractice, settleStreak } from '../engine/progress';
import { createRng } from '../engine/rng';
import { chooseTemplates, demote, promote } from '../engine/scheduler';

export type Tab = 'practice' | 'topics' | 'wins' | 'cards' | 'tools';
export type Screen = 'home' | 'drill' | 'done';
export type DayMark = 'on' | 'freeze' | 'off';
export type FontChoice = 'default' | 'hyperlegible';

export interface Settings {
  /** Questions per set. Short on purpose — a set should finish in under 3 minutes. */
  readonly setLength: number;
  readonly reduceMotion: boolean;
  readonly haptics: boolean;
  readonly font: FontChoice;
}

export const DEFAULT_SETTINGS: Settings = {
  setLength: 4,
  reduceMotion: false,
  haptics: true,
  font: 'default',
};

export interface Progress {
  readonly xp: number;
  readonly streak: number;
  readonly longest: number;
  readonly freezes: number;
  /**
   * Practice history by local calendar date. The week strip is derived from
   * this rather than stored, so it stays right when the app is opened days
   * later instead of freezing on whatever the last session rendered.
   */
  readonly days: Readonly<Record<string, DayMark>>;
  /** Local calendar date of the last completed set, YYYY-MM-DD. */
  readonly lastPracticeDay: string | null;
  readonly solved: number;
  readonly sets: number;
  readonly bench: number;
  readonly cleanRuns: readonly string[];
  /** Best correct count per topic. */
  readonly best: Readonly<Partial<Record<TopicId, number>>>;
  readonly earnedBadges: readonly string[];
  /** Leitner box per template id, 1–5. Drives which questions come round again. */
  readonly boxes: Readonly<Record<string, number>>;
  /** Template ids she parked. They jump the queue next time. */
  readonly parkedTemplates: readonly string[];
}

/**
 * A genuine first run starts at zero.
 *
 * The artifact prototype shipped seeded demo numbers (xp 340, streak 6, solved
 * 128) as its *initial* state, so a real first-time user inherited a six-day
 * streak they had not earned. Nothing here is fake.
 */
export const EMPTY_PROGRESS: Progress = {
  xp: 0,
  streak: 0,
  longest: 0,
  freezes: 2,
  days: {},
  lastPracticeDay: null,
  solved: 0,
  sets: 0,
  bench: 0,
  cleanRuns: [],
  best: {},
  earnedBadges: [],
  boxes: {},
  parkedTemplates: [],
};

/** What a question was drawn from — enough to rebuild it exactly. */
export interface Draw {
  readonly templateId: string;
  readonly seed: string;
}

export interface Session {
  readonly topicId: TopicId;
  /**
   * Local date the set was started. Completion is credited to this day, not to
   * whatever the clock says at the end — a set begun at 23:55 and finished at
   * 00:05 belongs to the day she sat down, which matters on a night shift.
   */
  readonly startedOn: string;
  readonly draws: readonly Draw[];
  readonly index: number;
  readonly response: Response;
  readonly revealed: boolean;
  readonly right: number;
  readonly parked: readonly number[];
  /** How many walk-through steps are currently showing. 0 = collapsed. */
  readonly stepsShown: number;
}

export interface SetResult {
  readonly topicId: TopicId;
  readonly right: number;
  readonly total: number;
  readonly parked: number;
  readonly newBadgeId: string | null;
}

export interface AppState {
  readonly screen: Screen;
  readonly tab: Tab;
  readonly session: Session | null;
  readonly result: SetResult | null;
  readonly progress: Progress;
  readonly settings: Settings;
  /** Badge earned by logging a bench challenge, shown inline on the home screen. */
  readonly benchBadgeId: string | null;
}

export const INITIAL_STATE: AppState = {
  screen: 'home',
  tab: 'practice',
  session: null,
  result: null,
  progress: EMPTY_PROGRESS,
  settings: DEFAULT_SETTINGS,
  benchBadgeId: null,
};

// ── Question access ──────────────────────────────────────────────────────────

/**
 * Rebuild the questions for a session.
 *
 * Sessions persist as template ids plus seeds rather than as generated objects.
 * That keeps the saved state tiny, survives content updates, and — because
 * generation is seeded and deterministic — brings back the exact same question
 * she was looking at, not a lookalike.
 */
export function sessionQuestions(session: Session): Question[] {
  return session.draws.map((draw) => {
    const template = TEMPLATE_BY_ID.get(draw.templateId);
    if (!template) throw new Error(`Session references a missing template: ${draw.templateId}`);
    return generate(template, draw.seed);
  });
}

export function currentQuestion(session: Session): Question {
  const questions = sessionQuestions(session);
  const q = questions[Math.min(session.index, questions.length - 1)];
  if (!q) throw new Error('Session has no questions');
  return q;
}

/** Draw a set, ordered by the Leitner scheduler. */
export function drawSet(
  topicId: TopicId,
  count: number,
  seed: string,
  progress: Progress,
): Draw[] {
  const rng = createRng(seed);
  const templateIds = chooseTemplates(
    topicId,
    count,
    { boxes: progress.boxes, parked: progress.parkedTemplates },
    rng,
  );
  return templateIds.map((templateId, i) => ({ templateId, seed: `${seed}:${i}` }));
}

// ── Actions ──────────────────────────────────────────────────────────────────

export type Action =
  | { type: 'hydrate'; state: Partial<AppState> }
  | { type: 'setTab'; tab: Tab }
  | { type: 'start'; topicId: TopicId; seed: string; today: string }
  | { type: 'settleDay'; today: string }
  | { type: 'respond'; response: Response }
  | { type: 'check' }
  | { type: 'park' }
  | { type: 'toggleWalk' }
  | { type: 'nextStep' }
  | { type: 'exit' }
  | { type: 'logBench' }
  | { type: 'resetProgress' }
  | { type: 'updateSettings'; patch: Partial<Settings> };

/** XP is small and generous: right answers pay more, but showing up always pays. */
const XP_RIGHT = 12;
const XP_ATTEMPT = 4;

export function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'hydrate':
      return { ...state, ...action.state };

    case 'setTab':
      return { ...state, tab: action.tab, screen: 'home' };

    case 'updateSettings':
      return { ...state, settings: { ...state.settings, ...action.patch } };

    case 'resetProgress':
      // Settings survive: someone who needed the high-legibility font before
      // still needs it, and making her find that switch again would be a
      // pointless second punishment.
      return {
        ...state,
        progress: EMPTY_PROGRESS,
        session: null,
        result: null,
        benchBadgeId: null,
        screen: 'home',
      };

    case 'settleDay':
      return { ...state, progress: settleStreak(state.progress, action.today) };

    case 'start': {
      const count = clampSetLength(state.settings.setLength);
      return {
        ...state,
        screen: 'drill',
        result: null,
        session: {
          topicId: action.topicId,
          startedOn: action.today,
          draws: drawSet(action.topicId, count, action.seed, state.progress),
          index: 0,
          response: null,
          revealed: false,
          right: 0,
          parked: [],
          stepsShown: 0,
        },
      };
    }

    case 'respond': {
      const s = state.session;
      // Locked after reveal — changing the answer once you have seen it is
      // not a thing, and silently allowing it would corrupt the score.
      if (!s || s.revealed) return state;
      return { ...state, session: { ...s, response: action.response } };
    }

    case 'check': {
      const s = state.session;
      if (!s) return state;
      if (!s.revealed) {
        const question = currentQuestion(s);
        const correct = isCorrect(question, s.response);
        const templateId = question.templateId;

        return {
          ...state,
          session: { ...s, revealed: true, right: s.right + (correct ? 1 : 0) },
          progress: {
            ...state.progress,
            xp: state.progress.xp + (correct ? XP_RIGHT : XP_ATTEMPT),
            boxes: {
              ...state.progress.boxes,
              [templateId]: correct ? promote(state.progress.boxes[templateId]) : demote(),
            },
            // Getting it right clears the parked flag: it has come back round
            // and she has answered it, which is the whole point of parking.
            parkedTemplates: correct
              ? state.progress.parkedTemplates.filter((id) => id !== templateId)
              : state.progress.parkedTemplates,
          },
        };
      }
      return advance(state, false);
    }

    case 'park': {
      if (!state.session) return state;
      return advance(state, true);
    }

    case 'toggleWalk': {
      const s = state.session;
      if (!s) return state;
      return { ...state, session: { ...s, stepsShown: s.stepsShown > 0 ? 0 : 1 } };
    }

    case 'nextStep': {
      const s = state.session;
      if (!s) return state;
      return { ...state, session: { ...s, stepsShown: s.stepsShown + 1 } };
    }

    case 'exit':
      return { ...state, screen: 'home', tab: 'practice', session: null };

    case 'logBench': {
      // The prototype incremented the counter but silently dropped the badge.
      const awarded = awardBadges({ ...state.progress, bench: state.progress.bench + 1 });
      return {
        ...state,
        progress: awarded.progress,
        benchBadgeId: awarded.newBadgeId,
      };
    }
  }
}

export function clampSetLength(value: number): number {
  return Math.max(2, Math.min(8, Math.round(value)));
}

/**
 * Move to the next question, or finish the set.
 *
 * Parking is free by design: no penalty, no dialog, and the question does not
 * count against her. It is the anti-shame mechanic and the whole reason a hard
 * question does not end the session.
 */
function advance(state: AppState, parkIt: boolean): AppState {
  const s = state.session;
  if (!s) return state;

  const parked = parkIt ? [...s.parked, s.index] : s.parked;
  const total = s.draws.length;

  // Remember which template was parked, not just its position in this set —
  // the position is meaningless once the set is over.
  const parkedTemplate = parkIt ? s.draws[s.index]?.templateId : undefined;
  const progressWithPark: Progress = parkedTemplate
    ? {
        ...state.progress,
        parkedTemplates: state.progress.parkedTemplates.includes(parkedTemplate)
          ? state.progress.parkedTemplates
          : [...state.progress.parkedTemplates, parkedTemplate],
      }
    : state.progress;

  if (s.index + 1 < total) {
    return {
      ...state,
      progress: progressWithPark,
      session: {
        ...s,
        index: s.index + 1,
        response: null,
        revealed: false,
        stepsShown: 0,
        parked,
      },
    };
  }

  const attempted = total - parked.length;
  const clean = s.right === total && parked.length === 0;
  const bestSoFar = state.progress.best[s.topicId] ?? 0;

  const counted: Progress = {
    ...progressWithPark,
    solved: progressWithPark.solved + attempted,
    sets: progressWithPark.sets + 1,
    best: { ...progressWithPark.best, [s.topicId]: Math.max(bestSoFar, s.right) },
    cleanRuns: clean
      ? [...progressWithPark.cleanRuns, `${s.topicId}:${progressWithPark.sets}`]
      : progressWithPark.cleanRuns,
  };

  // Order matters: the day has to be recorded before badges are checked, or a
  // streak badge earned by this very set would not appear until the next one.
  const withStreak = recordPractice(counted, s.startedOn);
  const awarded = awardBadges(withStreak);

  return {
    ...state,
    screen: 'done',
    session: null,
    result: {
      topicId: s.topicId,
      right: s.right,
      total,
      parked: parked.length,
      newBadgeId: awarded.newBadgeId,
    },
    progress: awarded.progress,
  };
}

// ── Derived ──────────────────────────────────────────────────────────────────

/** Percent complete for a topic, based on her best run through it. */
export function topicPercent(progress: Progress, topicId: TopicId, setLength: number): number {
  const best = progress.best[topicId] ?? 0;
  const target = Math.min(setLength, templatesForTopic(topicId).length || setLength);
  return Math.min(100, Math.round((best / target) * 100));
}

/**
 * What to offer next: the first topic whose prerequisites are all under way,
 * preferring untouched topics. The prototype just took the first untouched
 * topic in array order, which could hand her bolt circles before trig.
 */
export function nextTopic(progress: Progress): TopicId {
  const started = (id: TopicId): boolean => (progress.best[id] ?? 0) > 0;
  const ready = TOPICS.filter((t) => t.prereqs.every(started));
  const untouched = ready.find((t) => !started(t.id));
  if (untouched) return untouched.id;

  // Everything ready has been started — go back to the weakest one.
  const weakest = [...TOPICS].sort(
    (a, b) => (progress.best[a.id] ?? 0) - (progress.best[b.id] ?? 0),
  )[0];
  return weakest?.id ?? TOPICS[0]!.id;
}
